'use client';

import { useState } from 'react';
import Link from 'next/link';

export interface ReviewCard {
  id: string;
  rating: number;
  author: string;
  body: string;
  reply?: string;
  created_at: string;
  caseStudySlug?: string | null;
}

const REVIEWS_PER_PAGE = 20;

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="rating rating-sm">
      {[...Array(5)].map((_, i) => (
        <input
          key={i}
          type="radio"
          className="mask mask-star-2 bg-warning"
          checked={i === rating - 1}
          readOnly
        />
      ))}
    </div>
  );
}

function truncateName(name: string) {
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return name;
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// Build a compact list of page numbers, collapsing the middle into ellipses
// so the control stays usable on mobile even when there are many pages.
function getPageItems(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const items: (number | 'ellipsis')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) items.push('ellipsis');
  for (let p = start; p <= end; p++) items.push(p);
  if (end < total - 1) items.push('ellipsis');
  items.push(total);
  return items;
}

export default function ReviewList({ reviews }: { reviews: ReviewCard[] }) {
  const [page, setPage] = useState(1);
  const total = reviews.length;
  const totalPages = Math.max(1, Math.ceil(total / REVIEWS_PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * REVIEWS_PER_PAGE;
  const visible = reviews.slice(startIdx, startIdx + REVIEWS_PER_PAGE);
  const showingFrom = total === 0 ? 0 : startIdx + 1;
  const showingTo = startIdx + visible.length;

  const goTo = (p: number) => {
    const next = Math.min(Math.max(1, p), totalPages);
    setPage(next);
    if (typeof document !== 'undefined') {
      const el = document.getElementById('reviews-grid');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div>
      <div
        id="reviews-grid"
        className="scroll-mt-24 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto"
      >
        {visible.map((review) => (
          <div key={review.id} className="card bg-base-200 shadow-md hover:shadow-lg transition-shadow">
            <div className="card-body p-5">
              <div className="flex items-center justify-between mb-2">
                <StarRating rating={review.rating} />
                <span className="badge badge-sm badge-success gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Verified
                </span>
              </div>
              <p className="text-base-content/80 text-sm leading-relaxed">{review.body}</p>

              {/* Case study link (only if the linked order has a published showcase) */}
              {review.caseStudySlug && (
                <Link
                  href={`/blog/${review.caseStudySlug}`}
                  className="mt-3 flex items-center justify-between gap-2 rounded-md bg-primary/10 hover:bg-primary/20 px-3 py-2 text-sm font-semibold text-primary transition-colors"
                >
                  <span>Read the full case study</span>
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              )}

              {/* Owner reply */}
              {review.reply && (
                <div className="mt-3 pl-3 border-l-2 border-primary/40">
                  <p className="text-xs text-base-content/50 font-semibold mb-0.5">ChangeLyric replied:</p>
                  <p className="text-sm text-base-content/70 italic">{review.reply}</p>
                </div>
              )}

              <div className="mt-auto pt-3 flex items-center justify-between">
                <span className="font-semibold text-sm">{truncateName(review.author)}</span>
                <span className="text-xs text-base-content/50">{formatDate(review.created_at)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ============ PAGINATION ============ */}
      {totalPages > 1 && (
        <div className="mt-10 flex flex-col items-center gap-4">
          <p className="text-sm text-base-content/60">
            Showing {showingFrom}–{showingTo} of {total} reviews
          </p>
          <div className="flex flex-wrap justify-center items-center gap-2">
            <button
              type="button"
              onClick={() => goTo(safePage - 1)}
              disabled={safePage <= 1}
              className={`btn btn-sm btn-ghost ${safePage <= 1 ? 'btn-disabled opacity-40' : ''}`}
              aria-label="Previous page"
            >
              &larr; Prev
            </button>

            {getPageItems(safePage, totalPages).map((item, idx) =>
              item === 'ellipsis' ? (
                <span key={`e-${idx}`} className="px-1 text-base-content/50 select-none">
                  &hellip;
                </span>
              ) : (
                <button
                  key={item}
                  type="button"
                  onClick={() => goTo(item)}
                  className={`btn btn-sm ${item === safePage ? 'btn-primary' : 'btn-ghost'}`}
                  aria-current={item === safePage ? 'page' : undefined}
                >
                  {item}
                </button>
              )
            )}

            <button
              type="button"
              onClick={() => goTo(safePage + 1)}
              disabled={safePage >= totalPages}
              className={`btn btn-sm btn-ghost ${safePage >= totalPages ? 'btn-disabled opacity-40' : ''}`}
              aria-label="Next page"
            >
              Next &rarr;
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
